import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { initPushNotifications, removePushToken } from '../utils/pushNotifications';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Load settings first, before anything else
  useEffect(() => {
    loadSettings();
  }, []);

  // Then handle authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      const data = response.data;
      setSettings(data);
      
      // Apply custom colors immediately
      if (data.primary_color) {
        document.documentElement.style.setProperty('--primary-custom', data.primary_color);
      }
      if (data.accent_color) {
        document.documentElement.style.setProperty('--accent-custom', data.accent_color);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set default settings on error
      setSettings({
        company_name: '',
        subtitle: '',
        logo_url: '',
        primary_color: '#1e293b',
        accent_color: '#3b82f6'
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    // Apply colors immediately when settings are updated
    if (newSettings.primary_color) {
      document.documentElement.style.setProperty('--primary-custom', newSettings.primary_color);
    }
    if (newSettings.accent_color) {
      document.documentElement.style.setProperty('--accent-custom', newSettings.accent_color);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(user);
      
      // Initialize push notifications after login
      initPushNotifications();
      
      toast.success('Login realizado com sucesso!');
      return user;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
      throw error;
    }
  };

  const logout = () => {
    removePushToken();
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Show loading screen while settings are being fetched
  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, settings, updateSettings, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
